import { z } from 'zod';
import { foodAttributionSourceEnumSchema } from "./enums";

/**
 * Nutrition summary for dashboard
 */
export const nutritionLabelNutrientsSchema = z.object({
  saturatedFatG: z.number().min(0).optional(),
  transFatG: z.number().min(0).optional(),
  cholesterolMg: z.number().min(0).optional(),
  sodiumMg: z.number().min(0).optional(),
  dietaryFiberG: z.number().min(0).optional(),
  totalSugarsG: z.number().min(0).optional(),
  addedSugarsG: z.number().min(0).optional(),
  vitaminDMcg: z.number().min(0).optional(),
  calciumMg: z.number().min(0).optional(),
  ironMg: z.number().min(0).optional(),
  potassiumMg: z.number().min(0).optional(),
});
export type NutritionLabelNutrients = z.infer<typeof nutritionLabelNutrientsSchema>;

export const nutritionSummarySchema = z.object({
  calories: z.number(),
  protein: z.number(),
  carbs: z.number(),
  fat: z.number(),
  labelNutrients: nutritionLabelNutrientsSchema.optional(),
});
export type NutritionSummary = z.infer<typeof nutritionSummarySchema>;
export type Nutrition = NutritionSummary;

/**
* Food serving size entry
*/
export const servingSizeSchema = z
  .object({
    name: z.string().nullish(),
    weight: z.number().min(0),
    isWeightBased: z.boolean().optional(),
    nutrition: nutritionSummarySchema
  })
  ;
export type ServingSize = z.infer<typeof servingSizeSchema>;

export const foodItemServingSizeSchema = z.object({
  id: z.string(),
  label: z.string(),
  grams: z.number().nullable(),
  nutrition: nutritionSummarySchema,
  isWeightBased: z.boolean().optional(),
});
export type FoodItemServingSize = z.infer<typeof foodItemServingSizeSchema>;

/**
 * System food attribution photo
 */
export const foodAttributionPhotoSchema = z
  .object({
    id: z.string(),
    mimeType: z.string(),
    base64Data: z.string(),
    createdAt: z.string(),
  })
  ;
export type FoodAttributionPhoto = z.infer<typeof foodAttributionPhotoSchema>;

/**
 * System food attribution data
 */
export const foodAttributionSchema = z
  .object({
    id: z.string(),
    fdcId: z.number().nullable(),
    sourceType: foodAttributionSourceEnumSchema,
    rawJson: z.string().nullable(),
    photos: z.array(foodAttributionPhotoSchema),
    createdAt: z.string(),
  })
  ;
export type FoodAttribution = z.infer<typeof foodAttributionSchema>;

/**
 * Food item (system foods, admin managed)
 */
export const foodSchema = z
  .object({
    id: z.string(),
    upc: z.string(),
    gtins: z.array(z.string()).optional(),
    attributions: z.array(foodAttributionSourceEnumSchema),
    name: z.string(),
    description: z.string().nullable().optional(),
    brand: z.string().nullable(),
    servingSizes: z.array(servingSizeSchema),
    createdBy: z.string().nullable(),
    updatedBy: z.string().nullable(),
    approvedBy: z.string().nullable(),
    isVerified: z.boolean(),
    createdAt: z.string(),
    updatedAt: z.string(),
  })
  ;
export type Food = z.infer<typeof foodSchema>;



/**
 * User-created custom food item
 */
export const userFoodSchema = z.object({
  id: z.string(),
  userId: z.string(),
  name: z.string(),
  brand: z.string().nullable(),
  upc: z.string().nullable(),
  servings: z.object({
    name: z.string().nullish(),
    weight: z.number().min(0).nullable(),
    isWeightBased: z.boolean().optional(),
    nutrition: nutritionSummarySchema,
  }),
  isFavorite: z.boolean(),
  isArchived: z.boolean(),
  isProvisional: z.boolean(),
  createdAt: z.string()
});
export type UserFood = z.infer<typeof userFoodSchema>;

export const userFoodDocSchema = z.object({
  id: z.string(),
  userId: z.string(),
  name: z.string(),
  brand: z.string().nullable(),
  upc: z.string().nullable(),
  calories: z.number(),
  protein: z.number(),
  carbs: z.number(),
  fat: z.number(),
  servingSize: z.number().nullable(),
  servingUnit: z.string(),
  isFavorite: z.boolean(),
  isArchived: z.boolean(),
  isProvisional: z.boolean(),
  isSharedByOwner: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type UserFoodDocType = z.infer<typeof userFoodDocSchema>;

/**
 * Recipe ingredient with hydrated food snapshot
 */
export const recipeIngredientSchema = z.object({
  id: z.string(),
  kind: z.literal('food'),
  foodId: z.string(),
  foodSourceType: z.enum(['system', 'custom']),
  snapshot: z.object({
    name: z.string(),
    brand: z.string().nullable(),
    servingId: z.string(),
    servingLabel: z.string(),
    servingGrams: z.number().nullable(),
    servingNutrition: nutritionSummarySchema,
    nutritionPer100g: nutritionSummarySchema.optional(),
  }),
  mode: z.enum(['serving', 'weight']),
  quantity: z.number(),
  grams: z.number().nullable(),
});
export type RecipeIngredient = z.infer<typeof recipeIngredientSchema>;

export const recipeServingSizeSchema = z.object({
  id: z.string(),
  label: z.string(),
  grams: z.number(),
});
export type RecipeServingSize = z.infer<typeof recipeServingSizeSchema>;

export const recipeBodySchema = z.object({
  name: z.string(),
  yieldMode: z.enum(['weight', 'count']),
  servings: z.number(),
  servingLabel: z.string().nullable(),
  servingSizes: z.array(recipeServingSizeSchema).default([]),
  // Legacy clients may omit this field entirely; normalize omissions to null.
  preparedWeightGrams: z.preprocess((value) => (value === undefined ? null : value), z.number().nullable()),
  preparedWeightGramsAuto: z.number().optional(),
  preparedWeightGramsManual: z.number().optional(),
  displayWeightUnit: z.enum(['g', 'oz', 'lb']).optional(),
  ingredients: z.array(recipeIngredientSchema).default([]),
  isArchived: z.boolean(),
});
export type RecipeBody = z.infer<typeof recipeBodySchema>;

/**
 * Recipe as a document with fully hydrated ingredients
 */
export const recipeSchema = z
  .object({
    id: z.string(),
    userId: z.string(),
    body: recipeBodySchema,
    createdAt: z.string(),
    updatedAt: z.string(),
    deletedAt: z.string().nullable(),
  })
  ;

export type Recipe = z.infer<typeof recipeSchema>;
export type RecipeDocType = Recipe;

/**
 * Lightweight search result (no serving sizes, just summary macros)
 */
export const foodSearchResultSchema = z.object({
  id: z.string(),
  name: z.string(),
  brand: z.string().nullable(),
  kind: z.enum(['staple', 'branded']),
  calories: z.number(),
  protein: z.number(),
  carbs: z.number(),
  fat: z.number(),
  servingName: z.string(),
});
export type FoodSearchResult = z.infer<typeof foodSearchResultSchema>;

export const foodItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  brand: z.string().optional(),
  description: z.string().optional(),
  attribution: z.string().optional(),
  servingSizes: z.array(foodItemServingSizeSchema),
  isFavorite: z.boolean().optional(),
  isCustom: z.boolean().optional(),
  isProvisional: z.boolean().optional(),
  isArchived: z.boolean().optional(),
  isSharedByOwner: z.boolean().optional(),
  revisionOf: z.string().optional(),
  createdAt: z.string(),
  upc: z.string().optional(),
  pendingSystemFoodId: z.string().optional(),
});
export type FoodItem = z.infer<typeof foodItemSchema>;
